import request from './request'
import {
  getMockCouponMetaResponse,
  shouldUseCouponMock,
} from './couponCenterMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface CouponMetaOption {
  value: string
  label: string
}

type MetaOptionRaw = { value: string; label: string } | string

/** `/api/admin/coupon/meta` → `data.coupon_center` */
export interface CouponCenterMetaSection {
  coupon_type?: MetaOptionRaw[]
  status?: MetaOptionRaw[]
  tag?: MetaOptionRaw[]
  visibility?: MetaOptionRaw[]
  eligible_service?: MetaOptionRaw[]
  usage_platform?: MetaOptionRaw[]
  search_types?: MetaOptionRaw[]
  [key: string]: unknown
}

/** 与 library meta 相同：`{ label, value }[]` 结构 */
export interface CouponCenterMetaData {
  coupon_type?: MetaOptionRaw[]
  coupon_source?: MetaOptionRaw[]
  is_limit_amount?: MetaOptionRaw[]
  usage_platform?: MetaOptionRaw[]
  is_overlay?: MetaOptionRaw[]
  status?: MetaOptionRaw[]
  coupon_center?: CouponCenterMetaSection
  [key: string]: unknown
}

export interface CouponCenterMetaParams {
  page?: number
  page_size?: number
}

export const DEFAULT_COUPON_SEARCH_TYPES: CouponMetaOption[] = [
  { value: 'coupon_code', label: 'Coupon Code' },
  { value: 'coupon_name', label: 'Coupon Name' },
]

export const DEFAULT_COUPON_TYPES: CouponMetaOption[] = [
  { value: 'fixed_amount', label: 'Fixed Amount' },
  { value: 'percentage_off', label: 'Percentage Off' },
]

export const DEFAULT_ELIGIBLE_SERVICES: CouponMetaOption[] = [
  { value: 'all_services', label: 'All services' },
  { value: 'essential_care_subscription', label: 'Essential Care Subscription' },
  { value: 'enhanced_care_subscription', label: 'Enhanced Care Subscription' },
  { value: 'premium_care_subscription', label: 'Premium Care Subscription' },
]

export const DEFAULT_VISIBILITY_OPTIONS: CouponMetaOption[] = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
]

export const DEFAULT_AVAILABILITY_OPTIONS: CouponMetaOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

function normalizeMetaOption(
  entry: MetaOptionRaw,
  fallbacks: CouponMetaOption[],
): CouponMetaOption | null {
  if (typeof entry === 'string') {
    const value = entry.trim()
    if (!value) return null
    const fallback = fallbacks.find((o) => o.value === value)
    return { value, label: fallback?.label ?? value }
  }
  const value = String(entry.value ?? '').trim()
  const label = String(entry.label ?? '').trim()
  if (!value) return null
  return { value, label: label || value }
}

function buildOptions(
  raw: MetaOptionRaw[] | undefined,
  fallbacks: CouponMetaOption[],
): CouponMetaOption[] {
  if (!Array.isArray(raw) || !raw.length) {
    return [...fallbacks]
  }
  const options = raw
    .map((entry) => normalizeMetaOption(entry, fallbacks))
    .filter((o): o is CouponMetaOption => o != null)
  return options.length ? options : [...fallbacks]
}

function getCouponCenterSection(
  meta: CouponCenterMetaData | null | undefined,
): CouponCenterMetaSection | undefined {
  const center = meta?.coupon_center
  return center && typeof center === 'object' ? center : undefined
}

function pickMetaArray(
  meta: CouponCenterMetaData | null | undefined,
  centerKeys: string[],
  topLevelKeys: string[],
): MetaOptionRaw[] | undefined {
  const center = getCouponCenterSection(meta)
  if (center) {
    for (const key of centerKeys) {
      const value = center[key]
      if (Array.isArray(value) && value.length) {
        return value as MetaOptionRaw[]
      }
    }
  }

  for (const key of topLevelKeys) {
    const value = meta?.[key]
    if (Array.isArray(value) && value.length) {
      return value as MetaOptionRaw[]
    }
  }

  return undefined
}

export function buildCouponSearchTypes(
  meta: CouponCenterMetaData | null | undefined,
): CouponMetaOption[] {
  return buildOptions(
    pickMetaArray(meta, ['search_types'], ['search_types']),
    DEFAULT_COUPON_SEARCH_TYPES,
  )
}

/** Type → coupon_center.coupon_type
 *  以后端 meta 为准：已是字符串枚举则原样使用；仅兼容旧数字码 1/2
 */
export function buildCouponTypes(
  meta: CouponCenterMetaData | null | undefined,
): CouponMetaOption[] {
  return buildOptions(
    pickMetaArray(meta, ['coupon_type'], ['coupon_type']),
    DEFAULT_COUPON_TYPES,
  ).map((option) => {
    const value = option.value.trim().toLowerCase()
    if (value === '1') return { ...option, value: 'fixed_amount' }
    if (value === '2') return { ...option, value: 'percentage_off' }
    return option
  })
}

/** 是否为旧版 usage_platform（HM Care / All Services），不能当创建筛选项 */
function isLegacyEligibleServiceOptions(options: CouponMetaOption[]): boolean {
  if (!options.length) return true
  const hasSubscription = options.some((option) => {
    const value = option.value.trim().toLowerCase()
    const label = option.label.trim().toLowerCase()
    return (
      value.includes('subscription') ||
      value.includes('essential') ||
      value.includes('enhanced') ||
      value.includes('premium') ||
      label.includes('subscription')
    )
  })
  if (hasSubscription) return false
  return options.some((option) => {
    const value = option.value.trim().toLowerCase()
    const label = option.label.trim().toLowerCase()
    return value === '1' || value === '2' || label.includes('hm care')
  })
}

function normalizeEligibleServiceOption(option: CouponMetaOption): CouponMetaOption {
  const value = option.value.trim().toLowerCase()
  const label = option.label.trim().toLowerCase()
  if (
    value === '2' ||
    value === 'all' ||
    value === 'all_services' ||
    label === 'all services' ||
    label === 'all service'
  ) {
    return { ...option, value: 'all_services', label: option.label || 'All services' }
  }
  return option
}

/**
 * Eligible Services（创建 + 列表筛选）→ 图二：All services / Essential / Enhanced / Premium
 * 忽略旧版 meta 的 HM Care / All Services（图一）
 */
export function buildEligibleServices(
  meta: CouponCenterMetaData | null | undefined,
): CouponMetaOption[] {
  const fromCreateMeta = buildOptions(
    pickMetaArray(
      meta,
      ['eligible_services', 'eligible_service_options'],
      ['eligible_services', 'eligible_service_options'],
    ),
    [],
  ).map(normalizeEligibleServiceOption)

  if (fromCreateMeta.length && !isLegacyEligibleServiceOptions(fromCreateMeta)) {
    return fromCreateMeta
  }

  const fromCenterMeta = buildOptions(
    pickMetaArray(
      meta,
      ['eligible_service', 'usage_platform'],
      ['usage_platform', 'eligible_service'],
    ),
    [],
  ).map(normalizeEligibleServiceOption)

  if (fromCenterMeta.length && !isLegacyEligibleServiceOptions(fromCenterMeta)) {
    return fromCenterMeta
  }

  return DEFAULT_ELIGIBLE_SERVICES.map(normalizeEligibleServiceOption)
}

/** Visibility → coupon_center.tag / visibility */
export function buildVisibilityOptions(
  meta: CouponCenterMetaData | null | undefined,
): CouponMetaOption[] {
  return buildOptions(
    pickMetaArray(meta, ['visibility', 'tag'], ['visibility', 'tag']),
    DEFAULT_VISIBILITY_OPTIONS,
  ).map((option) => {
    const value = option.value.trim().toLowerCase()
    if (value === '1' || value === 'visible') return { ...option, value: 'visible' }
    if (value === '0' || value === 'hidden') return { ...option, value: 'hidden' }
    return option
  })
}

/** Availability → coupon_center.status */
export function buildAvailabilityOptions(
  meta: CouponCenterMetaData | null | undefined,
): CouponMetaOption[] {
  return buildOptions(
    pickMetaArray(meta, ['status'], ['status']),
    DEFAULT_AVAILABILITY_OPTIONS,
  ).map((option) => {
    const value = option.value.trim().toLowerCase()
    if (value === '1' || value === 'active') return { ...option, value: 'active' }
    if (value === '0' || value === 'inactive') return { ...option, value: 'inactive' }
    return option
  })
}

/** POST `/api/admin/coupon/meta`（与 technician / income-log meta 一致） */
export async function postCouponCenterMeta(
  params: CouponCenterMetaParams = {},
): Promise<ApiResponse<CouponCenterMetaData>> {
  if (shouldUseCouponMock()) {
    return getMockCouponMetaResponse() as ApiResponse<CouponCenterMetaData>
  }
  return request.post<ApiResponse<CouponCenterMetaData>>('/api/admin/coupon/meta', params)
}

/** @deprecated 使用 postCouponCenterMeta */
export const getCouponCenterMeta = postCouponCenterMeta

/** sessionStorage：Income Log 等跳转 Coupon Center 时预填搜索 */
export const COUPON_CENTER_LIST_PREFILL_KEY = 'couponCenterListPrefill'

export interface CouponCenterListPrefill {
  searchField: string
  searchQuery: string
}

export function writeCouponCenterListPrefill(payload: CouponCenterListPrefill): void {
  sessionStorage.setItem(COUPON_CENTER_LIST_PREFILL_KEY, JSON.stringify(payload))
}

export function readCouponCenterListPrefill(): CouponCenterListPrefill | null {
  try {
    const raw = sessionStorage.getItem(COUPON_CENTER_LIST_PREFILL_KEY)
    if (!raw) return null
    sessionStorage.removeItem(COUPON_CENTER_LIST_PREFILL_KEY)
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const o = parsed as CouponCenterListPrefill
    const searchField = typeof o.searchField === 'string' ? o.searchField.trim() : ''
    const searchQuery = typeof o.searchQuery === 'string' ? o.searchQuery.trim() : ''
    if (!searchField || !searchQuery) return null
    return { searchField, searchQuery }
  } catch {
    return null
  }
}
